import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import axios from "axios";
import { Link, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { MoonLoader } from "react-spinners";
import { alert_error, alert_success } from "../../AppContext.js";
import Language_i18 from "../../js/useNationalLanguage.jsx";

/*
 * UI refresh only:
 * - Preserve the existing data/API flow and routing.
 * - Preserve the original visual DNA: blue -> pink surrounding gradient,
 *   soft pink glass cards, rounded geometry, blue CTA, image-left/text-right layout.
 * - Modernize spacing, hierarchy, hover states, metadata and responsiveness.
 */

function ArrowRightIcon() {
    return (
        <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
            className="transition-transform duration-300 group-hover/cta:translate-x-0.5"
        >
            <path
                d="M5 12h14M13 6l6 6-6 6"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );
}

function CategoryMetaPill({ children, className = "" }) {
    return (
        <span
            className={[
                "inline-flex min-w-0 items-center rounded-full",
                "border border-white/60 bg-white/20",
                "px-2 py-1 text-[9px] font-medium leading-none text-[#5E6B7A]",
                "shadow-[inset_0_1px_0_rgba(255,255,255,0.35)] backdrop-blur-sm",
                className,
            ].join(" ")}
        >
            <span className="min-w-0 overflow-hidden text-ellipsis whitespace-nowrap">
                {children}
            </span>
        </span>
    );
}

const Category = () => {
    const { slug_2 } = useParams();
    const { lang } = Language_i18();
    const latest_version = useSelector((state) => state.latest_version);

    const [content, setcontent] = useState("");
    const [error, seterror] = useState("");
    const [imageErrors, setImageErrors] = useState({});

    const translations = {
        vi: {
            title: "Latest Insights",
            readMore: "Đọc thêm",
            writtenDate: "Ngày viết:",
            unknownError: "Đã xảy ra lỗi không xác định vui lòng liên hệ admin",
        },
        en: {
            title: "Latest Insights",
            readMore: "Read more",
            writtenDate: "Written date:",
            unknownError: "An unknown error occurred, please contact admin",
        },
    };

    const testFunction = (response) => {
        const info = response.data;
        const status = info.status;
        const message = info.message;

        if (status == 1) {
            const data = info.data;
            console.log(data);

            if (latest_version != 3) {
                alert_success(message);
            }

            setcontent(data);
        } else if (status == 0) {
            alert_error(message);
            seterror(message);
        } else {
            const unknownError =
                translations[lang]?.unknownError || translations.vi.unknownError;

            alert_error(unknownError);
            seterror(unknownError);
        }

        if (info.redirect != null && info.redirect != "") {
            setTimeout(() => {
                window.location.href = info.redirect;
            }, 1700);
        }
    };

    const fetchData = () => {
        if (!slug_2) return;

        axios
            .get(
                "https://hust.media/api/content/getdata_v2.php?uri=" +
                    slug_2 +
                    "&mode=category"
            )
            .then((response) => {
                testFunction(response);
            })
            .catch((requestError) => {
                alert_error(requestError);
            });
    };

    useEffect(() => {
        fetchData();
        // Keep the original page-loading behavior.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (!content && !error) {
        return (
            <div className="flex min-h-[34vh] items-center justify-center">
                <div className="rounded-3xl border border-white/50 bg-white/20 p-7 shadow-[0_18px_60px_rgba(67,76,116,0.12)] backdrop-blur-md">
                    <MoonLoader size={58} color="#2563EB" />
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="mx-auto mt-8 max-w-3xl px-4">
                <div className="rounded-3xl border border-white/55 bg-white/25 px-6 py-8 text-center shadow-[0_18px_60px_rgba(67,76,116,0.12)] backdrop-blur-md">
                    <div className="text-lg font-bold text-slate-800">{error}</div>
                </div>
            </div>
        );
    }

    return (
        <>
            <Helmet>
                <title>{translations[lang]?.title || translations.vi.title}</title>
            </Helmet>

            <section className="mx-auto w-full max-w-[1240px] px-3 pb-4 pt-3 sm:px-5 sm:pt-4 lg:px-6">
                {/* Editorial intro: same identity, cleaner hierarchy */}
                <header className="mx-auto mb-4 max-w-4xl text-center sm:mb-4">
                    <div className="mb-1.5 flex items-center justify-center gap-2">
                        <span className="h-px w-7 bg-slate-500/35" />
                        <span className="text-[10px] font-semibold uppercase tracking-[0.22em] text-slate-500/80">
                            Technical editorial
                        </span>
                        <span className="h-px w-7 bg-slate-500/35" />
                    </div>

                    <h1 className="text-[clamp(1.45rem,2vw,2rem)] font-extrabold tracking-[-0.025em] text-slate-900">
                        {translations[lang]?.title || translations.vi.title}
                    </h1>

                    <p className="mx-auto mt-1.5 max-w-4xl text-left text-[13px] leading-5 text-slate-600 sm:text-sm sm:leading-6">
                        A focused collection of practical technical articles on scalable
                        architecture and system optimization, covering hybrid deployment,
                        backend and frontend systems, integration workflows, performance
                        monitoring, and stable operation. The goal is to show how practical
                        technical decisions help web platforms remain reliable, scalable,
                        and easier to operate as they grow over time.
                    </p>
                </header>

                <div className="space-y-2.5 sm:space-y-3">
                    {content.map((post) => {
                        const thumbnailImage =
                            typeof post.thumbnail_image === "string"
                                ? post.thumbnail_image.trim()
                                : "";

                        const cardImage =
                            thumbnailImage !== "" ? thumbnailImage : post.image;

                        const hashName =
                            (post?.tips_hash_name || "").toString().trim() || "Hust Media";

                        const cardKey = post.uri || post.id || post.title;

                        return (
                            <article
                                key={cardKey}
                                className={[
                                    "group relative overflow-hidden rounded-[20px]",
                                    "border border-white/55 bg-pink-100/45",
                                    "shadow-[0_8px_22px_rgba(68,74,112,0.10)] backdrop-blur-[3px]",
                                    "transition-[transform,box-shadow,border-color,background-color] duration-300 ease-out",
                                    "hover:-translate-y-0.5 hover:border-white/80 hover:bg-pink-100/55",
                                    "hover:shadow-[0_12px_30px_rgba(68,74,112,0.14)]",
                                ].join(" ")}
                            >
                                {/* Very subtle future-tech accent without changing the palette */}
                                <div
                                    aria-hidden="true"
                                    className="pointer-events-none absolute inset-x-7 top-0 h-px bg-gradient-to-r from-transparent via-white/80 to-transparent"
                                />

                                <div className="grid grid-cols-1 sm:grid-cols-[176px_minmax(0,1fr)] lg:grid-cols-[188px_minmax(0,1fr)]">
                                    {/* Image */}
                                    <div className="p-2.5 sm:p-3">
                                        <div
                                            className={[
                                                "flex h-[112px] items-center justify-center overflow-hidden rounded-[15px]",
                                                "border border-white/70 bg-white/75 p-2.5",
                                                "shadow-[0_8px_24px_rgba(74,84,122,0.08)]",
                                            ].join(" ")}
                                        >
                                            {!cardImage || imageErrors[cardKey] ? (
                                                <div className="flex h-full w-full items-center justify-center px-4 text-center text-sm font-semibold leading-5 text-slate-600">
                                                    {post.title}
                                                </div>
                                            ) : (
                                                <img
                                                    src={cardImage}
                                                    alt={post.title}
                                                    loading="lazy"
                                                    className="block h-full w-full object-contain transition-transform duration-500 ease-out group-hover:scale-[1.025]"
                                                    onError={() =>
                                                        setImageErrors((prev) => ({
                                                            ...prev,
                                                            [cardKey]: true,
                                                        }))
                                                    }
                                                />
                                            )}
                                        </div>
                                    </div>

                                    {/* Content */}
                                    <div className="min-w-0 px-3 pb-3 sm:flex sm:items-center sm:px-0 sm:py-3 sm:pr-4">
                                        <div className="w-full border-l-0 border-slate-500/40 sm:border-l-2 sm:pl-4">
                                            <div className="min-w-0">
                                                <h2 className="text-[15px] font-bold leading-[1.22] tracking-[-0.01em] text-slate-900 sm:text-[16px] lg:text-[17px]">
                                                    {post.title}
                                                </h2>

                                                <p className="mt-1 text-[12px] leading-[1.45] text-slate-700 sm:text-[12.5px] sm:leading-[1.5]">
                                                    {post.description}
                                                </p>
                                            </div>

                                            <div className="mt-2 flex flex-wrap items-center gap-2">
                                                <Link
                                                    reloadDocument
                                                    to={`/next/tech/${post.uri}`}
                                                    style={{ textDecoration: "none" }}
                                                    className="group/cta inline-flex"
                                                >
                                                    <span
                                                        className={[
                                                            "inline-flex items-center gap-2.5 rounded-full",
                                                            "rounded-full border border-white/75 bg-sky-100/45 px-3 py-1",
                                                            "text-[12px] font-bold text-slate-700 backdrop-blur-xl backdrop-saturate-150 sm:text-[13px]",
                                                            "shadow-[inset_0_1px_1px_rgba(255,255,255,0.9),0_5px_14px_rgba(67,76,116,0.14)]",
                                                            "transition-all duration-300",
                                                            "hover:border-white hover:bg-sky-100/60 hover:shadow-[inset_0_1px_1px_rgba(255,255,255,0.95),0_8px_20px_rgba(67,76,116,0.2)]",
                                                            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/80 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent",
                                                        ].join(" ")}
                                                    >
                                                        {translations[lang]?.readMore ||
                                                            translations.vi.readMore}
                                                        <ArrowRightIcon />
                                                    </span>
                                                </Link>

                                                <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5">
                                                    <CategoryMetaPill
                                                        className="max-w-full"
                                                    >
                                                        <span className="sm:hidden">
                                                            {String(post.createdate || "").split(" ")[0]}
                                                        </span>
                                                        <span className="hidden sm:inline">
                                                            {post.createdate}
                                                        </span>
                                                    </CategoryMetaPill>

                                                    <CategoryMetaPill
                                                        className="max-w-[180px] sm:max-w-[240px]"
                                                    >
                                                        {hashName}
                                                    </CategoryMetaPill>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </article>
                        );
                    })}
                </div>
            </section>
        </>
    );
};

export default Category;
